"use client";

import { SITE_TIMEZONE } from "@/lib/site";
import { useEffect, useState, useCallback } from "react";
import { useTranslations } from "next-intl";
import { RefreshCw, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

interface Probe {
  name: string;
  url: string;
  descKey: string;
}

const PROBES: Probe[] = [
  {
    name: "GET /matrix",
    url: "https://zaruvia.com/api/public/matrix/15/3/1990",
    descKey: "matrix",
  },
  {
    name: "GET /life-path",
    url: "https://zaruvia.com/api/public/life-path",
    descKey: "lifePathList",
  },
  {
    name: "GET /life-path/7",
    url: "https://zaruvia.com/api/public/life-path/7",
    descKey: "lifePath7",
  },
  {
    name: "GET /api/public (root)",
    url: "https://zaruvia.com/api/public",
    descKey: "root",
  },
  {
    name: "GET /api/health",
    url: "https://zaruvia.com/api/health",
    descKey: "health",
  },
  {
    name: "GET /widget/v1",
    url: "https://zaruvia.com/widget/v1/zaruvia-widget.js",
    descKey: "widget",
  },
];

type Status = "checking" | "ok" | "slow" | "down";

interface Result {
  status: Status;
  latencyMs: number | null;
  httpStatus: number | null;
  checkedAt: Date | null;
}

const SLOW_THRESHOLD_MS = 800;
const TIMEOUT_MS = 8000;

async function probe(url: string): Promise<Result> {
  const started = performance.now();
  try {
    const res = await fetch(url, {
      method: "GET",
      signal: AbortSignal.timeout(TIMEOUT_MS),
      cache: "no-store",
    });
    const latency = Math.round(performance.now() - started);
    const ok = res.ok;
    return {
      status: ok ? (latency > SLOW_THRESHOLD_MS ? "slow" : "ok") : "down",
      latencyMs: latency,
      httpStatus: res.status,
      checkedAt: new Date(),
    };
  } catch {
    return {
      status: "down",
      latencyMs: null,
      httpStatus: null,
      checkedAt: new Date(),
    };
  }
}

const STATUS_META: Record<Status, { color: string; bg: string; border: string }> = {
  checking: { color: "text-stone-500", bg: "bg-stone-50", border: "border-stone-200" },
  ok:       { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  slow:     { color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  down:     { color: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
};

export function StatusChecker() {
  const t = useTranslations("statusChecker");
  const [results, setResults] = useState<Record<string, Result>>(() =>
    Object.fromEntries(
      PROBES.map((p) => [
        p.name,
        { status: "checking" as Status, latencyMs: null, httpStatus: null, checkedAt: null },
      ]),
    ),
  );

  const runChecks = useCallback(async () => {
    setResults((prev) => {
      const next: Record<string, Result> = { ...prev };
      for (const p of PROBES) {
        next[p.name] = { ...next[p.name], status: "checking" };
      }
      return next;
    });
    const ps = PROBES.map(async (p) => {
      const r = await probe(p.url);
      setResults((prev) => ({ ...prev, [p.name]: r }));
    });
    await Promise.all(ps);
  }, []);

  useEffect(() => {
    runChecks();
    const interval = setInterval(runChecks, 30_000); // авто-обновление каждые 30с
    return () => clearInterval(interval);
  }, [runChecks]);

  // Общий статус
  // Пока хотя бы один endpoint в состоянии "checking" И никаких реальных
  // результатов ещё нет — показываем "Проверяем" вместо "Замедление работы".
  const values = Object.values(results);
  const checking = values.some((r) => r.status === "checking");
  const hasAnyResult = values.some((r) => r.checkedAt !== null);
  const allOk = values.every((r) => r.status === "ok");
  const anyDown = values.some((r) => r.status === "down");

  const overall: Status | "initial" = !hasAnyResult
    ? "initial"
    : anyDown
      ? "down"
      : allOk
        ? "ok"
        : "slow";
  const overallMeta = overall === "initial" ? STATUS_META.checking : STATUS_META[overall];

  return (
    <div>
      {/* Overall status banner */}
      <div
        className={[
          "rounded-2xl border-2 p-6 mb-6",
          overallMeta.border,
          overallMeta.bg,
        ].join(" ")}
      >
        <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {overall === "initial" && (
              <Loader2 className={`w-8 h-8 ${overallMeta.color} animate-spin`} />
            )}
            {overall === "ok" && (
              <CheckCircle2 className={`w-8 h-8 ${overallMeta.color}`} />
            )}
            {overall === "slow" && (
              <AlertCircle className={`w-8 h-8 ${overallMeta.color}`} />
            )}
            {overall === "down" && (
              <AlertCircle className={`w-8 h-8 ${overallMeta.color}`} />
            )}
            <div>
              <div className={`text-xl font-bold ${overallMeta.color}`}>
                {overall === "initial" && t("overall.initial")}
                {overall === "ok" && t("overall.ok")}
                {overall === "slow" && t("overall.slow")}
                {overall === "down" && t("overall.down")}
              </div>
              <div className="text-xs text-stone-600 mt-0.5">
                {t("autoRefresh")}
              </div>
            </div>
          </div>
          <button
            onClick={runChecks}
            disabled={checking}
            className="inline-flex items-center gap-2 bg-white border border-stone-200 hover:border-violet-200 hover:bg-violet-50 text-stone-700 hover:text-violet-700 font-semibold px-4 py-2 rounded-xl text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${checking ? "animate-spin" : ""}`} />
            {t("checkNow")}
          </button>
        </div>
      </div>

      {/* Per-endpoint table */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <th className="text-left font-semibold text-stone-700 py-3 px-4">Endpoint</th>
              <th className="text-left font-semibold text-stone-700 py-3 px-4">{t("colStatus")}</th>
              <th className="text-left font-semibold text-stone-700 py-3 px-4">Latency</th>
              <th className="text-left font-semibold text-stone-700 py-3 px-4 hidden sm:table-cell">
                HTTP
              </th>
              <th className="text-left font-semibold text-stone-700 py-3 px-4 hidden md:table-cell">
                {t("colChecked")}
              </th>
            </tr>
          </thead>
          <tbody>
            {PROBES.map((p) => {
              const r = results[p.name];
              const meta = STATUS_META[r.status];
              return (
                <tr key={p.name} className="border-b border-stone-100 last:border-0">
                  <td className="py-3 px-4">
                    <div className="font-mono text-sm font-medium text-stone-800">
                      {p.name}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      {t(`probes.${p.descKey}`)}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={[
                        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border",
                        meta.color,
                        meta.bg,
                        meta.border,
                      ].join(" ")}
                    >
                      {r.status === "checking" && (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      )}
                      {t(`statusLabel.${r.status}`)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-stone-700">
                      {r.latencyMs !== null ? `${r.latencyMs} ms` : "—"}
                    </span>
                  </td>
                  <td className="py-3 px-4 hidden sm:table-cell">
                    <span className="font-mono text-sm text-stone-600">
                      {r.httpStatus ?? "—"}
                    </span>
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell text-[11px] text-stone-500">
                    {r.checkedAt
                      ? r.checkedAt.toLocaleTimeString("es-ES", { timeZone: SITE_TIMEZONE })
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-stone-500 mt-4 leading-relaxed">
        {t("note")}
      </p>
    </div>
  );
}
